import { ListStatus, ListType } from "@/enum/list.enum";
import { List } from "@/pages/Private/pages/List/schema/list";

/** The status dot shown on a row — mirrors the mockup's active / paused / none. */
export type ScopeRowStatus = "active" | "paused" | "none";

/** Flattened, render-ready shape derived from a full `List` for one dropdown row. */
export interface ScopeListRow {
	id: number;
	title: string;
	/** ON_GOING lists show the running clock icon. */
	isRunning: boolean;
	status: ScopeRowStatus;
	assigned: number;
	total: number | null;
	archived: boolean;
	/** False when the list holds leads, but none of them on the surface being filtered. */
	hasRelevantLeads: boolean;
}

/** Set equality used to decide whether the staged selection differs from applied. */
export const setsEqual = (a: Set<number>, b: Set<number>): boolean => {
	if (a.size !== b.size) {
		return false;
	}

	for (const value of a) {
		if (!b.has(value)) {
			return false;
		}
	}

	return true;
};

/** Map a list's `ListStatus` onto the dropdown's three-state status dot. */
const toRowStatus = (status: List["status"]): ScopeRowStatus => {
	if (status === ListStatus.ACTIVE) {
		return "active";
	}

	if (status === ListStatus.PAUSED) {
		return "paused";
	}

	return "none";
};

/**
 * Coerce a lean count to an integer. The lean endpoint's MySQL `SUM()` aggregates
 * arrive as strings (e.g. `"59"`), so plain `+` would concatenate instead of add.
 */
const toCount = (value: number | string | null | undefined): number => {
	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Leads shown on this row's numerator. The messaging surfaces (Inbox/ReadyToSend/Outbox)
 * pass an assignStatus relevance set to the lean endpoint, which returns `relevantLeads`
 * scoped to exactly that set — so the badge matches what the page actually lists (e.g. the
 * Inbox's replies). Falls back to `assignedLeads` for any consumer without a relevance set.
 */
const relevantCount = (list: List): number => toCount(list.relevantLeads ?? list.assignedLeads);

/**
 * Total leads in a list, used as the badge denominator. Prefers the lean endpoint's
 * `totalLeads` (every included lead). Falls back to summing the three disjoint bucket
 * columns (`found` new/draft, `savedForLater`, `assignedLeads` in-outreach) for consumers
 * that don't return `totalLeads`, and to `null` when no count data exists so the badge hides.
 */
const rowTotal = (list: List): number | null => {
	if (list.totalLeads != null) {
		return toCount(list.totalLeads);
	}

	const { found, savedForLater, assignedLeads } = list;

	if (found == null && savedForLater == null && assignedLeads == null) {
		return null;
	}

	return toCount(found) + toCount(savedForLater) + toCount(assignedLeads);
};

/** Project a full `List` into the lean row shape the dropdown renders. */
export const mapListToRow = (list: List): ScopeListRow => ({
	id: list.id,
	title: list.name,
	isRunning: list.type === ListType.ON_GOING,
	status: toRowStatus(list.status),
	assigned: relevantCount(list),
	total: rowTotal(list),
	archived: list.archived ?? false,
	hasRelevantLeads: relevantCount(list) > 0,
});

export interface PartitionedScopeRows {
	/** Lists with something on this surface — the ones the user is normally after. */
	active: ScopeListRow[];
	/**
	 * Lists whose leads all sit on another surface. Kept and shown in their own section rather than
	 * hidden, because a vanished list reads as a broken filter (PREC-3834).
	 */
	irrelevant: ScopeListRow[];
	archived: ScopeListRow[];
}

/**
 * Split the loaded lists into the three dropdown sections. Archived wins over relevance: an
 * archived list already sits behind its own collapsed section, and splitting it again would bury it.
 */
export const partitionLists = (lists: List[]): PartitionedScopeRows => {
	const active: ScopeListRow[] = [];
	const irrelevant: ScopeListRow[] = [];
	const archived: ScopeListRow[] = [];

	for (const list of lists) {
		const row = mapListToRow(list);

		if (row.archived) {
			archived.push(row);
		} else if (row.hasRelevantLeads) {
			active.push(row);
		} else {
			irrelevant.push(row);
		}
	}

	return { active, irrelevant, archived };
};
